"use client"

import { useState, useEffect } from "react"
import { X, ChevronDown, ChevronUp, User, Users, Search } from "lucide-react"

const EnhancedGroupCreationModal = ({ isOpen, onClose, contacts, onCreateGroup, isCreating, currentUserId }) => {
  const [groupName, setGroupName] = useState("")
  const [selectedContacts, setSelectedContacts] = useState({})
  const [expandedClass, setExpandedClass] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")

  useEffect(() => {
    if (!isOpen) {
      setGroupName("")
      setSelectedContacts({})
      setExpandedClass(null)
      setSearchTerm("")
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen && contacts) {
      console.log("GroupCreationModal contacts data:", contacts)
      console.log("Current user ID:", currentUserId)
    }
  }, [isOpen, contacts, currentUserId])

  const toggleExpand = (classId) => {
    setExpandedClass(expandedClass === classId ? null : classId)
  }

  const handleContactSelect = (contact) => {
    setSelectedContacts((prev) => ({
      ...prev,
      [contact.id]: prev[contact.id] ? undefined : contact,
    }))
  }

const handleCreateGroup = () => {
  const trimmedName = groupName.trim();

  // Require at least one English alphabet character (a-z or A-Z)
  const hasEnglishLetter = /[a-zA-Z]/.test(trimmedName);

  if (!trimmedName || !hasEnglishLetter) {
    alert("Group name must contain at least one English letter and cannot be only special characters.");
    return;
  }

  if (Object.keys(selectedContacts).length > 0) {
    onCreateGroup(trimmedName, Object.values(selectedContacts).filter(Boolean));
  }
}


  const filterContacts = (contacts, searchTerm) => {
    if (!searchTerm.trim()) return contacts

    return contacts
      .map((classData) => {
        const filteredStudents = (classData.enrolledStudent || []).filter((student) =>
          student.studentName?.toLowerCase().includes(searchTerm.toLowerCase()),
        )

        const teacherMatches = classData.teacherName?.toLowerCase().includes(searchTerm.toLowerCase())

        if (teacherMatches || filteredStudents.length > 0) {
          return {
            ...classData,
            enrolledStudent: filteredStudents,
          }
        }
        return null
      })
      .filter(Boolean)
  }

  if (!isOpen) return null

  if (!contacts || !Array.isArray(contacts)) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Create New Group</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X size={24} />
            </button>
          </div>
          <div className="text-center text-gray-500">
            <p>No contacts available</p>
            <p className="text-xs mt-2">Loading contacts...</p>
          </div>
        </div>
      </div>
    )
  }

  const filteredContacts = filterContacts(contacts, searchTerm)

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Create New Group</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
            <span className="sr-only">Close</span>
          </button>
        </div>

        {/* Group Name Input */}
        <input
          type="text"
          placeholder="Group Name"
          value={groupName}
          onChange={(e) => setGroupName(e.target.value)}
          className="w-full px-3 py-2 border rounded-md mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />

        {/* Search Input */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
          <input
            type="text"
            placeholder="Search contacts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Selected Contacts Count */}
        {Object.keys(selectedContacts).length > 0 && (
          <div className="mb-4 p-2 bg-blue-50 rounded-md">
            <p className="text-sm text-blue-700">{Object.keys(selectedContacts).length} contact(s) selected</p>
          </div>
        )}

        <div className="mb-4 flex-grow overflow-y-auto">
          <h3 className="font-semibold mb-2">Select Participants:</h3>
          <div className="space-y-2">
            {filteredContacts.length === 0 ? (
              <div className="text-center text-gray-500 py-4">
                {searchTerm ? "No contacts found matching your search" : "No contacts available"}
              </div>
            ) : (
              filteredContacts.map((classData, idx) => {
                if (!classData) return null

                const classValue = classData.classValue || classData.class || "Unknown"
                const section = classData.section || "Unknown"
                const classId = `${classValue}-${section}`
                const isExpanded = expandedClass === classId

                const teacherId = classData.teacherId || classData.teacher?.id
                const teacherName = classData.teacherName || classData.teacher?.name || classData.teacher?.teacherName
                const showTeacher = teacherId && teacherId !== currentUserId

                const students = classData.enrolledStudent || classData.students || classData.enrolledStudents || []
                const filteredStudents = students.filter((student) => {
                  if (!student) return false
                  const studentId = student.studentId || student.id || student._id
                  return studentId && studentId !== currentUserId
                })

                if (!showTeacher && filteredStudents.length === 0) {
                  return null
                }

                return (
                  <div key={idx} className="border rounded-lg overflow-hidden">
                    <button
                      className="w-full flex justify-between items-center p-3 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      onClick={() => toggleExpand(classId)}
                    >
                      <span className="font-semibold">
                        {classValue} - Section {section}
                      </span>
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-500">
                          {(showTeacher ? 1 : 0) + filteredStudents.length} contacts
                        </span>
                        {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                      </div>
                    </button>
                    {isExpanded && (
                      <div className="p-3 bg-white">
                        {showTeacher && (
                          <>
                            <h4 className="font-bold text-sm mb-2 flex items-center">
                              <User className="mr-2" size={16} /> Teacher
                            </h4>
                            <div className="mb-2">
                              <label className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={!!selectedContacts[teacherId]}
                                  onChange={() =>
                                    handleContactSelect({
                                      id: teacherId,
                                      name: teacherName || "Unknown Teacher",
                                      type: "teacher",
                                    })
                                  }
                                  className="form-checkbox h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                                />
                                <span className="text-sm">{teacherName || "Unknown Teacher"}</span>
                              </label>
                            </div>
                          </>
                        )}
                        {filteredStudents.length > 0 && (
                          <>
                            <h4 className="font-bold text-sm mb-2 flex items-center">
                              <Users className="mr-2" size={16} /> Students ({filteredStudents.length})
                            </h4>
                            <div className="space-y-1 max-h-40 overflow-y-auto">
                              {filteredStudents.map((student, studentIdx) => {
                                const studentId = student.studentId || student.id || student._id
                                const studentName =
                                  student.studentName || student.name || student.firstName || "Unknown Student"

                                return (
                                  <label
                                    key={studentId || studentIdx}
                                    className="flex items-center space-x-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={!!selectedContacts[studentId]}
                                      onChange={() =>
                                        handleContactSelect({
                                          id: studentId,
                                          name: studentName,
                                          type: "student",
                                        })
                                      }
                                      className="form-checkbox h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                                    />
                                    <span className="text-sm">{studentName}</span>
                                  </label>
                                )
                              })}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )
              })
            )}
          </div>
        </div>

        <button
          onClick={handleCreateGroup}
          disabled={isCreating || !groupName.trim() || Object.keys(selectedContacts).length === 0}
          className={`w-full py-3 rounded-md font-medium transition-colors ${
            isCreating || !groupName.trim() || Object.keys(selectedContacts).length === 0
              ? "bg-gray-400 cursor-not-allowed text-gray-600"
              : "bg-blue-500 hover:bg-blue-600 text-white"
          }`}
        >
          {isCreating ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Creating Group...
            </div>
          ) : (
            `Create Group (${Object.keys(selectedContacts).length} selected)`
          )}
        </button>
      </div>
    </div>
  )
}

export default EnhancedGroupCreationModal
